import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import {ToastContainer} from "react-toastify";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faQuestionCircle} from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import AppComponentCardHeader from "../common/AppComponentCardHeader";
import TrainingTestsEditor from "./TrainingTestsEditor";
import TrainingTestAssignments from "./TrainingTestAssignments";
import "../../../css/TrainingTests.css";

const emptyForm = () => ({
  id: null,
  employeeId: "",
  trainingId: "",
  attemptedAt: new Date().toISOString().slice(0, 16),
  score: "",
  passed: true,
  comments: ""
});

const employeeLabel = employee => [employee?.firstName, employee?.lastName].filter(Boolean).join(" ").trim();

const employeeDepartmentNames = employee => {
  const names = new Set();
  if (employee?.department?.name) names.add(employee.department.name);
  (employee?.departmentNames || []).forEach(name => {
    if (name) names.add(name);
  });
  (employee?.employeePositions || []).forEach(position => {
    if (position?.active === false) return;
    const name = position?.department?.name || position?.position?.department?.name;
    if (name) names.add(name);
  });
  return [...names];
};

const employeeBelongsToDepartment = (employee, departmentId) => {
  if (!departmentId) return true;
  const targetId = Number(departmentId);
  if (Number(employee?.department?.id) === targetId) return true;
  return (employee?.employeePositions || []).some(position => {
    if (position?.active === false) return false;
    const id = position?.department?.id || position?.position?.department?.id;
    return Number(id) === targetId;
  });
};

const SearchableEmployeeSelect = ({employees, value, onChange, placeholder = "Wszyscy pracownicy"}) => {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = employees.find(employee => String(employee.id) === String(value));

  const filtered = useMemo(() => {
    const phrase = query.trim().toLocaleLowerCase("pl");
    if (!phrase) return employees;
    return employees.filter(employee => {
      const haystack = [
        employeeLabel(employee),
        employee.login || "",
        ...employeeDepartmentNames(employee)
      ].join(" ").toLocaleLowerCase("pl");
      return haystack.includes(phrase);
    });
  }, [employees, query]);

  useEffect(() => {
    const close = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openList = () => {
    setQuery("");
    setOpen(true);
  };

  const choose = employee => {
    onChange(employee ? String(employee.id) : "");
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="training-employee-picker" ref={wrapperRef}>
      <input
        type="text"
        className="form-control"
        value={open ? query : selected ? employeeLabel(selected) : ""}
        placeholder={selected ? employeeLabel(selected) : placeholder}
        onFocus={openList}
        onClick={openList}
        onChange={event => {
          setQuery(event.target.value);
          setOpen(true);
          if (!event.target.value) onChange("");
        }}
        autoComplete="off"
      />
      {open && (
        <div className="training-employee-picker-menu">
          <button type="button" className="training-employee-picker-option training-employee-picker-all" onClick={() => choose(null)}>
            {placeholder}
          </button>
          {filtered.map(employee => (
            <button
              type="button"
              key={employee.id}
              className={`training-employee-picker-option ${String(employee.id) === String(value) ? "selected" : ""}`}
              onClick={() => choose(employee)}
            >
              <span>{employeeLabel(employee)}</span>
              {employeeDepartmentNames(employee).length > 0 && (
                <small>{employeeDepartmentNames(employee).join(" · ")}</small>
              )}
            </button>
          ))}
          {filtered.length === 0 && <div className="training-employee-picker-empty">Brak pracowników pasujących do wpisanej frazy.</div>}
        </div>
      )}
    </div>
  );
};

const Trainingx = () => {
  const axiosPrivate = useAxiosPrivate();
  const [attempts, setAttempts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [formDepartmentId, setFormDepartmentId] = useState("");

  const sortedEmployees = useMemo(() => [...employees].sort((a, b) =>
    `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`, "pl")
  ), [employees]);

  const formEmployees = useMemo(() => sortedEmployees.filter(employee =>
    employeeBelongsToDepartment(employee, formDepartmentId)
  ), [sortedEmployees, formDepartmentId]);

  const sortedDepartments = useMemo(() => [...departments].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "pl")
  ), [departments]);

  const loadReferenceData = async () => {
    try {
      const [employeesResponse, trainingsResponse, departmentsResponse] = await Promise.all([
        axiosPrivate.get("/employees"),
        axiosPrivate.get("/trainings"),
        axiosPrivate.get("/departments")
      ]);
      setEmployees(employeesResponse.data || []);
      setTrainings(trainingsResponse.data || []);
      setDepartments(departmentsResponse.data || []);
    } catch (error) {
      toastError(error?.response?.data || "Nie udało się pobrać danych pomocniczych testów szkoleniowych");
    }
  };

  const loadAttempts = async () => {
    setLoading(true);
    try {
      const params = filterEmployeeId ? {employeeId: filterEmployeeId} : {};
      const response = await axiosPrivate.get("/employee-training-test-attempts", {params});
      setAttempts(response.data || []);
    } catch (error) {
      toastError(error?.response?.data || "Nie udało się pobrać wyników testów szkoleniowych");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferenceData();
  }, []);

  useEffect(() => {
    loadAttempts();
  }, [filterEmployeeId]);

  const resetForm = () => {
    setForm(emptyForm());
    setFormDepartmentId("");
  };

  const changeFormDepartment = event => {
    const departmentId = event.target.value;
    const selectedEmployee = employees.find(employee => String(employee.id) === String(form.employeeId));
    setFormDepartmentId(departmentId);
    if (selectedEmployee && !employeeBelongsToDepartment(selectedEmployee, departmentId)) {
      setForm(prev => ({...prev, employeeId: ""}));
    }
  };

  const saveAttempt = async event => {
    event.preventDefault();
    if (!form.employeeId || !form.trainingId || !form.attemptedAt) {
      toastError("Pracownik, szkolenie i data testu są wymagane");
      return;
    }

    const score = form.score === "" ? null : Number(form.score);
    if (score !== null && (Number.isNaN(score) || score < 0 || score > 100)) {
      toastError("Wynik testu musi mieścić się w zakresie od 0 do 100%");
      return;
    }

    const payload = {
      employeeId: Number(form.employeeId),
      trainingId: Number(form.trainingId),
      attemptedAt: form.attemptedAt.length === 16 ? `${form.attemptedAt}:00` : form.attemptedAt,
      score,
      passed: Boolean(form.passed),
      comments: form.comments || null
    };

    try {
      if (form.id) {
        await axiosPrivate.put(`/employee-training-test-attempts/${form.id}`, payload);
        toastSuccess("Zaktualizowano wynik testu szkoleniowego");
      } else {
        await axiosPrivate.post("/employee-training-test-attempts", payload);
        toastSuccess("Dodano wynik testu szkoleniowego");
      }
      resetForm();
      await loadAttempts();
    } catch (error) {
      toastError(error?.response?.data || "Nie udało się zapisać wyniku testu szkoleniowego");
    }
  };

  const editAttempt = attempt => {
    const employee = employees.find(item => String(item.id) === String(attempt.employeeId));
    setForm({
      id: attempt.id,
      employeeId: String(attempt.employeeId),
      trainingId: String(attempt.trainingId),
      attemptedAt: attempt.attemptedAt ? attempt.attemptedAt.slice(0, 16) : "",
      score: attempt.score ?? "",
      passed: Boolean(attempt.passed),
      comments: attempt.comments || ""
    });
    setFormDepartmentId(employee?.department?.id ? String(employee.department.id) : "");
  };

  const deleteAttempt = async id => {
    try {
      await axiosPrivate.delete(`/employee-training-test-attempts/${id}`);
      if (form.id === id) resetForm();
      toastSuccess("Usunięto wynik testu szkoleniowego");
      await loadAttempts();
    } catch (error) {
      toastError(error?.response?.data || "Nie udało się usunąć wyniku testu szkoleniowego");
    }
  };

  const formatDate = value => value ? value.replace("T", " ").slice(0, 16) : "";

  return (
    <div id="layoutSidenav_content" className="training-tests-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Testy szkoleniowe"/>
      <div className="container-fluid px-4">
        <TrainingTestsEditor/>
        <TrainingTestAssignments/>

        <div className="card shadow-sm p-3 mb-4 bg-white rounded-2 training-attempt-card">
          <div className="training-card-heading">
            <div>
              <h6 className="mb-1">Rejestracja wyniku testu</h6>
              <span>Ręczne dodanie lub korekta wyniku testu pracownika.</span>
            </div>
          </div>

          <form onSubmit={saveAttempt}>
            <div className="training-attempt-main-grid">
              <div className="training-form-field">
                <label className="form-label">Dział</label>
                <select className="form-select" value={formDepartmentId} onChange={changeFormDepartment}>
                  <option value="">Wszystkie działy</option>
                  {sortedDepartments.map(department => (
                    <option key={department.id} value={department.id}>{department.name}</option>
                  ))}
                </select>
              </div>

              <div className="training-form-field">
                <label className="form-label">Pracownik</label>
                <SearchableEmployeeSelect
                  employees={formEmployees}
                  value={form.employeeId}
                  onChange={employeeId => setForm(prev => ({...prev, employeeId}))}
                  placeholder="-- wybierz pracownika --"
                />
              </div>

              <div className="training-form-field">
                <label className="form-label">Szkolenie</label>
                <select className="form-select" value={form.trainingId} onChange={event => setForm({...form, trainingId: event.target.value})}>
                  <option value="">-- wybierz --</option>
                  {trainings.map(training => <option key={training.id} value={training.id}>{training.name}</option>)}
                </select>
              </div>

              <div className="training-form-field">
                <label className="form-label">Data i godzina</label>
                <input type="datetime-local" className="form-control" value={form.attemptedAt} onChange={event => setForm({...form, attemptedAt: event.target.value})}/>
              </div>

              <div className="training-form-field">
                <label className="form-label training-result-label">
                  <span>Wynik testu (%)</span>
                  <span className="form_tooltip">
                    <FontAwesomeIcon icon={faQuestionCircle}/>
                    <span className="form_tooltip_text">
                      Wynik procentowy w skali 0–100. Test wykonywany przez pracownika jest liczony jako udział poprawnie rozwiązanych pytań w całym teście. Status „Zaliczony” jest wtedy ustalany automatycznie na podstawie progu zaliczenia zdefiniowanego dla testu.
                    </span>
                  </span>
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="form-control"
                    value={form.score}
                    onChange={event => setForm({...form, score: event.target.value})}
                  />
                  <span className="input-group-text">%</span>
                </div>
              </div>

              <div className="training-form-field training-passed-field">
                <label className="form-label">Zaliczony</label>
                <div className="training-passed-control">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.passed}
                    onChange={event => setForm({...form, passed: event.target.checked})}
                  />
                </div>
              </div>
            </div>

            <div className="training-attempt-bottom-grid">
              <div className="training-form-field">
                <label className="form-label">Uwagi</label>
                <input type="text" className="form-control" value={form.comments} onChange={event => setForm({...form, comments: event.target.value})}/>
              </div>
              <div className="training-attempt-actions">
                <Button type="submit" variant="primary">{form.id ? "Zapisz" : "Dodaj"}</Button>
                {form.id && <Button type="button" variant="outline-secondary" onClick={resetForm}>Anuluj</Button>}
              </div>
            </div>
          </form>
        </div>

        <div className="card shadow-sm p-3 mb-5 bg-white rounded-2 training-history-card">
          <div className="training-history-toolbar">
            <div>
              <h6 className="mb-1">Historia wyników</h6>
              <span className="text-muted small">Możesz ograniczyć historię do wybranego pracownika.</span>
            </div>
            <div className="training-history-employee-filter">
              <label className="form-label">Pracownik</label>
              <SearchableEmployeeSelect
                employees={sortedEmployees}
                value={filterEmployeeId}
                onChange={setFilterEmployeeId}
                placeholder="Wszyscy pracownicy"
              />
            </div>
          </div>

          {loading ? (
            <div>Ładowanie danych...</div>
          ) : attempts.length === 0 ? (
            <div className="text-muted">Brak zarejestrowanych wyników testów.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead>
                <tr>
                  <th>Data</th>
                  <th>Pracownik</th>
                  <th>Szkolenie</th>
                  <th>Wynik (%)</th>
                  <th>Status</th>
                  <th>Uwagi</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                {attempts.map(attempt => (
                  <tr key={attempt.id}>
                    <td>{formatDate(attempt.attemptedAt)}</td>
                    <td>{attempt.employeeFirstName} {attempt.employeeLastName}</td>
                    <td>{attempt.testTitle || attempt.trainingName}{attempt.testVersion ? <div className="small text-muted">Wersja testu: {attempt.testVersion}</div> : null}</td>
                    <td>{attempt.score == null ? "-" : `${attempt.score}%`}</td>
                    <td><span className={attempt.passed ? "text-success fw-bold" : "text-danger fw-bold"}>{attempt.passed ? "ZALICZONY" : "NIEZALICZONY"}</span></td>
                    <td>{attempt.comments || ""}{attempt.testId ? <div className="small text-muted">Wynik automatyczny · historia audytowa</div> : null}</td>
                    <td className="text-nowrap text-end">
                      {attempt.testId ? <span className="badge bg-secondary">Tylko odczyt</span> : <>
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => editAttempt(attempt)}>Edytuj</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => deleteAttempt(attempt.id)}>Usuń</Button>
                      </>}
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Trainingx;
